import React, {FC, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {InitialPage} from "../../../../../utils/constans/pagination/pagination";
import {
  clearFreighterOrderRequests,
  getFreighterOrderRequests
} from "../../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducer";
import {CustomResult} from "../../../../../components/UI/AntCustom/CustomResult";
import {OrderRequestCard} from "./OrderRequestCard";
import {IOrderRequestFilters} from "../RequestFromAgentsPage";
import styled from "styled-components";
import {useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {useSearchParams} from "react-router-dom";
import isNumeric from "antd/es/_util/isNumeric";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import CloseIcon from "../../../../../media/icons/close.svg?react"
import {mediaScreenSizes} from "../../../../../utils/constans/styles/screenSizes";
import {OrderTypeEnum} from "../../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducerTypes";
import {OrderRequestCardSkeleton} from "../../../../../components/UI/SkeletonLoaders/OrderRequestCardSkeleton";
import {clearCreateEvent} from "../../../../../store/reducers/scheduleReducer/scheduleReducer";
import {message} from "antd";
import {defineMessages} from "react-intl";

const OrderRequestCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  
  @media(max-width: ${mediaScreenSizes.mobile}) {
    margin-top: 16px;
  }
`;

const OrderRequestsTabPaneMessages = defineMessages({
  addToSchedule: {
    id: "orderRequestCardMessages.addToSchedule",
    defaultMessage: "Added to schedule",
  },
});

interface IOrderRequestsTabPane {
  orderTypes: OrderTypeEnum[];
  filters: IOrderRequestFilters;
  isArchival?: boolean;
  isFavorite?: boolean;
  expectedCount?: number;
}

export const OrderRequestsTabPane: FC<IOrderRequestsTabPane> = ({orderTypes, filters, isArchival = false, isFavorite = false, expectedCount}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderRequestIdFromParams = isNumeric(searchParams.get('orderRequestId')) ? Number(searchParams.get('orderRequestId')) : undefined;
  const {
    items: allItems,
    totalCount,
    isLoading,
  } = useAppSelector(state => state.freighterRequests.itemsObject)
  
  const {
    isLoading: isScheduleCreating,
    isSuccess: isScheduleCreateSuccess,
    errorMessage: scheduleCreateErrorMessage,
  } = useAppSelector(state => state.schedule.create);
  
  // Filter items for favorites tab - only show items with isFavorite=true
  const items = isFavorite ? allItems.filter(item => item.isFavorite) : allItems;
  
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);

  // Обработка успешного создания события в расписании (один раз на уровне родителя)
  useEffect(() => {
    if (!isScheduleCreating && isScheduleCreateSuccess) {
      message.success(intl.formatMessage(OrderRequestsTabPaneMessages.addToSchedule));
      dispatch(clearCreateEvent());
    }
    if (!isScheduleCreating && scheduleCreateErrorMessage) {
      message.error(scheduleCreateErrorMessage);
      dispatch(clearCreateEvent());
    }
  }, [isScheduleCreating, isScheduleCreateSuccess, scheduleCreateErrorMessage, intl, dispatch]);

  // Calculate skeleton count: use expectedCount if available, otherwise 1
  const skeletonCount = expectedCount !== undefined && expectedCount > 0 ? expectedCount : 1;

  useEffect(() => {
    dispatch(getFreighterOrderRequests({
      page: currentPage,
      isArchival: isArchival,
      orderTypes: orderTypes,
      filters: filters,
      orderRequestIds: orderRequestIdFromParams ? [orderRequestIdFromParams] : undefined,
      isFavorite: isFavorite,
    }));

    return () => {
      dispatch(clearFreighterOrderRequests());
    }
  }, [isArchival, orderTypes, filters, orderRequestIdFromParams, isFavorite]);

  // Refetch list when window/tab regains focus so operator sees broker-accepted status without full page refresh
  useEffect(() => {
    const onFocus = () => {
      dispatch(getFreighterOrderRequests({
        page: currentPage,
        isArchival: isArchival,
        orderTypes: orderTypes,
        filters: filters,
        orderRequestIds: orderRequestIdFromParams ? [orderRequestIdFromParams] : undefined,
        isFavorite: isFavorite,
      }));
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [currentPage, isArchival, orderTypes, filters, orderRequestIdFromParams, isFavorite, dispatch]);

  const loadMore = () => {
    dispatch(getFreighterOrderRequests({
      page: currentPage + 1,
      isArchival: isArchival,
      orderTypes: orderTypes,
      filters: filters,
      isFavorite: isFavorite,
    }));

    setCurrentPage(prev => prev + 1);
  }

  const removeIdFromSearchParams = () => {
    if (searchParams.has('orderRequestId')) {
      searchParams.delete('orderRequestId')
      setSearchParams(searchParams);
    }
  }

  // Функция для обновления списка заявок
  const refreshOrderRequests = () => {
    dispatch(getFreighterOrderRequests({
      page: currentPage,
      isArchival: isArchival,
      orderTypes: orderTypes,
      filters: filters,
      orderRequestIds: orderRequestIdFromParams ? [orderRequestIdFromParams] : undefined,
      isFavorite: isFavorite,
    }));
  };

  // Show skeleton loaders while loading
  if (isLoading && items.length === 0) {
    return (
      <>
        {orderRequestIdFromParams && <CustomButton icon={<CloseIcon />} onClick={removeIdFromSearchParams}>{intl.formatMessage(CommonFormMessages.showAll)}</CustomButton>}
        <OrderRequestCardsWrapper>
          {[...Array(skeletonCount)].map((_, index) => (
            <OrderRequestCardSkeleton key={`skeleton-${index}`} />
          ))}
        </OrderRequestCardsWrapper>
      </>
    );
  }

  return (
    <>
      {orderRequestIdFromParams && <CustomButton icon={<CloseIcon />} onClick={removeIdFromSearchParams}>{intl.formatMessage(CommonFormMessages.showAll)}</CustomButton>}

      {items.length === 0
        ? <CustomResult title={intl.formatMessage(CommonFormMessages.noData)}/>
        : <OrderRequestCardsWrapper>{items.map((or) => {
          return <OrderRequestCard key={or.id} orderRequest={or} onOfferSubmitSuccess={refreshOrderRequests}/>
        })}</OrderRequestCardsWrapper>
      }

      {items.length > 0 && totalCount > items.length &&
        <CustomButton
          loading={isLoading}
          type={'primary'}
          onClick={loadMore}
        >
          {intl.formatMessage(CommonFormMessages.showMore)}
        </CustomButton>}
    </>
  );
};
